/**
 * Admin System Configuration Screen.
 * Shows system stats and allows config changes.
 */

import { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@/app/PageHeader';
import { LoadingOverlay } from '@/app/Spinner';
import { classifyError } from '@/api/errors';
import * as systemApi from '@/api/systemApi';
import type { SystemConfig, SystemStats } from '@/api/types';
import { MAX_REPORTS_MAX, MAX_REPORTS_MIN, validateMaxReportsPerUser } from './validateMaxReports';

export function SystemScreen() {
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const maxReportsBounds = useMemo(() => ({ min: MAX_REPORTS_MIN, max: MAX_REPORTS_MAX }), []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configData, statsData] = await Promise.all([
        systemApi.getSystemConfig(),
        systemApi.getSystemStats(),
      ]);
      setConfig(configData);
      setStats(statsData);
    } catch (err) {
      setError(classifyError(err).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async (data: {
    maintenance_mode?: boolean;
    registration_enabled?: boolean;
    max_reports_per_user?: number;
  }) => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const result = await systemApi.updateSystemConfig(data);
      setConfig(result.config);
      setValidationError(null);
      setSaveMsg({ type: 'success', text: 'Configuration saved successfully.' });
    } catch (err) {
      setSaveMsg({ type: 'error', text: classifyError(err).message });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const maintenanceModeOn = (form.elements.namedItem('maintenance_mode') as HTMLInputElement)
      .checked;
    if (maintenanceModeOn && config && !config.maintenance_mode) {
      if (
        !window.confirm(
          'Enable maintenance mode?\n\nThis will prevent all normal users from accessing the service.',
        )
      ) {
        return;
      }
    }
    const registrationEnabled = (
      form.elements.namedItem('registration_enabled') as HTMLInputElement
    ).checked;
    const maxReportsRaw = (form.elements.namedItem('max_reports_per_user') as HTMLInputElement)
      .value;

    // PHASE 8A — A8-P1-2: client-side validation that strictly mirrors the
    // backend Zod schema (integer, >= 1, <= 10000). Invalid input MUST NOT
    // reach the PATCH API.
    const validation = validateMaxReportsPerUser(maxReportsRaw);
    if (!validation.ok) {
      setSaveMsg(null);
      setValidationError(validation.reason);
      // Keep the user on the System page; focus the offending field.
      const field = form.elements.namedItem('max_reports_per_user') as HTMLInputElement | null;
      field?.focus();
      return;
    }

    setValidationError(null);
    handleSave({
      maintenance_mode: maintenanceModeOn,
      registration_enabled: registrationEnabled,
      max_reports_per_user: validation.value,
    });
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  if (loading) return <LoadingOverlay />;

  if (error) {
    return (
      <div className="page-content">
        <PageHeader title="System" />
        <div className="alert alert--error">{error}</div>
      </div>
    );
  }

  if (!config || !stats) return null;

  return (
    <div className="page-content">
      <PageHeader title="System" subtitle="Platform configuration and health monitoring." />

      {/* System Stats */}
      <div className="stats-grid mb-24">
        <div className="stat-card">
          <div className="stat-card__label">Uptime</div>
          <div className="stat-card__value">{formatUptime(stats.uptime_seconds)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">API Calls Today</div>
          <div className="stat-card__value">{stats.total_api_calls_today.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Storage Used</div>
          <div className="stat-card__value">{formatBytes(stats.total_storage_bytes)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Active Users (24h)</div>
          <div className="stat-card__value">{stats.active_users_24h.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Error Rate</div>
          <div className="stat-card__value">{stats.error_rate_percent.toFixed(2)}%</div>
        </div>
      </div>

      {/* Configuration */}
      <div className="card mb-24">
        <div className="card__header">
          <span className="card__title">Configuration</span>
        </div>
        <div className="card__body">
          <form onSubmit={handleSubmit} className="stack">
            <div className="row gap-16 items-center">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="maintenance_mode"
                  defaultChecked={config.maintenance_mode}
                />
                <span>Maintenance Mode</span>
              </label>
              <span className="muted text-sm">When enabled, users cannot access the app.</span>
            </div>

            <div className="row gap-16 items-center">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="registration_enabled"
                  defaultChecked={config.registration_enabled}
                />
                <span>User Registration</span>
              </label>
              <span className="muted text-sm">Allow new users to sign up.</span>
            </div>

            <div className="field" style={{ maxWidth: 300 }}>
              <label htmlFor="max_reports">Max Reports per User</label>
              <input
                type="number"
                id="max_reports"
                name="max_reports_per_user"
                className="input"
                defaultValue={config.max_reports_per_user}
                min={maxReportsBounds.min}
                max={maxReportsBounds.max}
                step={1}
                inputMode="numeric"
                aria-invalid={validationError !== null}
                aria-describedby={validationError ? 'max_reports_error' : undefined}
                onChange={() => {
                  // Clear the validation error once the user edits the field.
                  if (validationError) setValidationError(null);
                }}
              />
              <span className="muted text-sm">
                Whole number between {maxReportsBounds.min} and {maxReportsBounds.max}.
              </span>
              {validationError && (
                <div
                  id="max_reports_error"
                  className="alert alert--error"
                  role="alert"
                  data-testid="max-reports-validation-error"
                >
                  {validationError}
                </div>
              )}
            </div>

            {saveMsg && <div className={`alert alert--${saveMsg.type}`}>{saveMsg.text}</div>}

            <div className="row gap-8">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? <span className="spinner" /> : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Environment Info */}
      <div className="card">
        <div className="card__header">
          <span className="card__title">Environment</span>
        </div>
        <div className="card__body">
          <table className="data-table" style={{ maxWidth: 500 }}>
            <tbody>
              <tr>
                <td>Environment</td>
                <td>
                  <span className="pill">{config.environment}</span>
                </td>
              </tr>
              <tr>
                <td>App Version</td>
                <td className="font-bold">{config.app_version}</td>
              </tr>
              <tr>
                <td>API Version</td>
                <td>{config.api_version}</td>
              </tr>
              <tr>
                <td>Storage</td>
                <td>
                  {formatBytes(config.current_storage_bytes)} /{' '}
                  {formatBytes(config.max_storage_bytes)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
